import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { ProjectGroupPermission, ProjectPermissionConstants, ProjectTypeConstants, UserType } from "../../../constants";
import { FileType, SelectData, TabsList } from "../../../models";
import { Origin } from "../../../models/shared/origin";
import { IStateOrigin } from "../../../navigators/navigateStateObjects";
import {
  GetGroupDetailsResponse as GetPublicGroupDetailsResponse,
  getProjectAggregate as getPublicProjectAggregate,
  searchProjects,
} from "../../../service/publicquery";
import { getAggregateIssuances, GetGroupDetailsResponse, getProjectAggregate } from "../../../service/query";
import { Status } from "../../../service/Shared";
import { useAuth } from "../../../useAuth";
import { distinctBy } from "../../../utils";
import {
  getLandholdingDashboardRoute,
  getLandholdingDocumentsRoute,
  getLandholdingInventoryRoute,
  getLandholdingLocationRoute,
  getLandholdingTasksRoute,
  getManageLandholdingRoute,
} from "../../../utils/routes";
import { ChartData, dataGridMapFilterCriteria, ProfileMenuItemType } from "../../../widget";
import { LandholdingContext } from "./LandholdingContext";

interface UseLandholdingReturnData {
  landholdingUuid: string | undefined;
  landholdingDetails: GetGroupDetailsResponse | undefined;
  publicLandholdingDetails: GetPublicGroupDetailsResponse | undefined;
  handleBackClick: () => void;
  tabsList: TabsList;
  backButtonText: string;
  projectsChartData: ChartData;
  areaChartData: ChartData;
  projectsChartColours: string[][];
  areaChartColours: string[][];
  isInventoryChartLoading: boolean;
  inventoryChartData: ChartData;
  selectedUnitMeasurementData: string;
  updateInventoryChartData: (unitMeasurement: string) => void;
  unitMeasurementDropDownData: SelectData;
  currentUserType?: UserType;
  readGroupProjectsPermission: ProjectGroupPermission | undefined;
  quickActionsMenuOptionsList: ProfileMenuItemType[];
  showQuickActionsMenu: boolean;
  setShowQuickActionsMenu: (show: boolean) => void;
  publicProjectsDevelopers: {
    name?: string;
    icon?: string;
  }[];
}

export const useLandholding = (): UseLandholdingReturnData => {
  const navigate = useNavigate();
  const {
    landholdingUuid,
    landholdingDetails,
    publicLandholdingDetails,
    readGroupProjectsPermission,
    hasGroupPermission,
  } = useContext(LandholdingContext);

  const { currentUserType } = useAuth();
  const { state } = useLocation();
  const stateOrigin = state?.origin as IStateOrigin;
  const [projectsChartData, setProjectsChartData] = useState<ChartData>({});
  const [areaChartData, setAreaChartData] = useState<ChartData>({});
  const [inventoryChartData, setInventoryChartData] = useState<ChartData>({});
  const [projectsChartColours, setProjectsChartColours] = useState<string[][]>([]);
  const [areaChartColours, setAreaChartColours] = useState<string[][]>([]);
  const [unitMeasurementDropDownData, setUnitMeasurementDropDownData] = useState<SelectData>([]);
  const [selectedUnitMeasurementData, setSelectedUnitMeasurementData] = useState<string>("");
  const [isInventoryChartLoading, setIsInventoryChartLoading] = useState(true);
  const [showQuickActionsMenu, setShowQuickActionsMenu] = useState(false);
  const [publicProjectsDevelopers, setPublicProjectsDevelopers] = useState<
    {
      name?: string;
      icon?: string;
    }[]
  >([]);

  let backButtonText = stateOrigin?.goBackText;

  if (!backButtonText) {
    backButtonText = "Back";
  }

  const handleBackClick = (): void => {
    if (stateOrigin?.from) {
      navigate(stateOrigin.from);
      return;
    }

    navigate(-1);
  };

  const projectFilterValue = `[${(landholdingDetails?.projects ?? publicLandholdingDetails?.projects ?? [])
    .map((project) => project.uuid)
    .join(",")}]`;
  const projectsSignature = (landholdingDetails?.projects ?? publicLandholdingDetails?.projects ?? [])
    .map((project) => `${project.uuid}:${project.projectType.displayName}`)
    .join("|");

  const fetchInventoryData = useCallback(
    async (unitMeasurement: string): Promise<void> => {
      setIsInventoryChartLoading(true);
      const filterCriteria = dataGridMapFilterCriteria([]);
      const filters = [];

      if (unitMeasurement) {
        filters.push({
          filterBy: "measurementType",
          filterOperator: "eq",
          filterValue: unitMeasurement,
        });
      }

      filters.push({
        filterBy: "project",
        filterOperator: "in",
        filterValue: projectFilterValue,
      });

      await getAggregateIssuances({
        filters,
        groupBy: [landholdingDetails ? "projectNameMeasurement" : "verifiedOrPending"],
        isReadableProject: false,
        paging: {
          beforeCursor: null,
          afterCursor: null,
          limit: 1000,
        },
        filter: {
          results: {
            ...filterCriteria,
          },
        },
      })
        .then((response) => {
          if (response.status === Status.Success && response.data) {
            if (landholdingDetails) {
              const { measurementTypes } = response.data.aspects;
              const dropDownSelectData: SelectData = [
                { key: "", value: "Overall" },
                ...measurementTypes
                  .filter((value: string) => value !== "Carbon")
                  .map((value: string) => ({
                    key: value,
                    value,
                  })),
              ];

              const carbonItem = measurementTypes.includes("Carbon") ? { key: "Carbon", value: "Carbon" } : null;

              if (carbonItem) {
                dropDownSelectData.splice(1, 0, carbonItem);
              }

              setUnitMeasurementDropDownData(dropDownSelectData);
              setInventoryChartData(
                response.data.results.reduce(
                  (previous, current) => ({
                    Available: previous.Available + current.availableUnits,
                    Reserved: previous.Reserved + current.reservedUnits,
                    Sold: previous.Sold + current.soldUnits,
                  }),
                  { Available: 0, Reserved: 0, Sold: 0 }
                )
              );
            } else {
              setInventoryChartData(
                response.data.results.reduce<ChartData>((previous, current) => {
                  // eslint-disable-next-line no-param-reassign
                  previous[current.groupByFieldContent1 ?? "Undefined"] =
                    current.soldUnits + current.reservedUnits + current.availableUnits;
                  return previous;
                }, {})
              );
            }
          }
        })
        .finally(() => {
          setIsInventoryChartLoading(false);
        });
    },
    [projectFilterValue]
  );

  const fetchProjects = useCallback(async (): Promise<void> => {
    const filterCriteria = dataGridMapFilterCriteria([]);
    filterCriteria.landholdings = {
      some: {
        groupUuid: {
          operator: "eq",
          value: landholdingUuid,
        },
      },
    };

    await searchProjects({
      paging: {
        beforeCursor: null,
        afterCursor: null,
        limit: 50,
      },
      /* eslint-enable */
      filter: { results: filterCriteria },
    }).then((response) => {
      if (response.status === Status.Success && response.data) {
        setPublicProjectsDevelopers(
          distinctBy(
            response.data.results
              .map((project) => ({
                name: project.developer?.displayName ?? undefined,
                icon: project.developer?.files.find((file) => file.type === FileType.SmallLogo)?.file.url,
              }))
              .filter((developer): developer is { name: string; icon: string | undefined } => !!developer.name),
            (developer) => developer.name
          )
        );
      }
    });
  }, [landholdingUuid]);

  const updateInventoryChartData = useCallback(
    (unitMeasurement: string): void => {
      setSelectedUnitMeasurementData(unitMeasurement);
      fetchInventoryData(unitMeasurement);
    },
    [fetchInventoryData]
  );

  const quickActionsMenuOptionsList = useMemo<ProfileMenuItemType[]>(() => {
    if (!hasGroupPermission(ProjectPermissionConstants.MANAGE_GROUP)) return [];

    return [
      {
        id: 1,
        value: "Manage landholding",
        action: () => {
          navigate(
            getManageLandholdingRoute(landholdingUuid || "", currentUserType, `origin=${Origin.LandholdingDetailsPage}`)
          );
        },
      },
    ];
  }, [currentUserType, landholdingUuid, hasGroupPermission, navigate]);

  useEffect(() => {
    if (!landholdingDetails?.groupGuid && !publicLandholdingDetails?.groupGuid) {
      return;
    }

    const loadDashboardData = async (): Promise<void> => {
      const projects = landholdingDetails?.projects ?? publicLandholdingDetails?.projects ?? [];
      const areaAggregateResponse = landholdingDetails
        ? await getProjectAggregate({
            filterBy: "group",
            filterOperator: "eq",
            filterValue: (landholdingDetails?.groupGuid ?? publicLandholdingDetails?.groupGuid) as string,
            groupBy: "projectType",
            aggregation: "sum",
            aggregate: "projectAreaHa",
          })
        : await getPublicProjectAggregate({
            filterBy: "group",
            filterOperator: "eq",
            filterValue: publicLandholdingDetails?.groupGuid as string,
            groupBy: "projectType",
            aggregation: "sum",
            aggregate: "projectAreaHa",
          });

      if (areaAggregateResponse.status === Status.Success && areaAggregateResponse.data) {
        setAreaChartData(
          areaAggregateResponse.data.reduce<ChartData>((previous, current) => {
            // eslint-disable-next-line no-param-reassign
            previous[current.key] = current.value;
            return previous;
          }, {})
        );
        setAreaChartColours(
          areaAggregateResponse.data.map((project) => ProjectTypeConstants.PROJECT_TYPE_CHART_COLOR_MAP[project.key])
        );
      }

      if (projects.length > 0) {
        setProjectsChartData(
          projects.reduce<ChartData>((previous, current) => {
            const projectType = current.projectType.displayName;
            // eslint-disable-next-line no-param-reassign
            previous[projectType] = (previous[projectType] || 0) + 1;
            return previous;
          }, {})
        );

        setProjectsChartColours(
          projects.reduce((accumulator: string[][], project) => {
            const color = ProjectTypeConstants.PROJECT_TYPE_CHART_COLOR_MAP[project.projectType.displayName];
            if (!accumulator.includes(color)) {
              accumulator.push(color);
            }
            return accumulator;
          }, [])
        );
      } else {
        setProjectsChartData({});
        setProjectsChartColours([]);
      }

      // We need the extra data we get from this api in some places in the FE
      if (publicLandholdingDetails) {
        await fetchProjects();
      }

      await fetchInventoryData(selectedUnitMeasurementData);
    };

    loadDashboardData().catch(() => {
      setIsInventoryChartLoading(false);
    });
  }, [
    fetchInventoryData,
    landholdingDetails?.groupGuid,
    publicLandholdingDetails?.groupGuid,
    projectsSignature,
    selectedUnitMeasurementData,
  ]);

  const tabsList = useMemo((): TabsList => {
    const tabState = stateOrigin
      ? {
          origin: stateOrigin,
        }
      : undefined;

    return landholdingDetails
      ? [
          {
            label: "Details",
            route: getLandholdingDashboardRoute(landholdingUuid || "", currentUserType),
            state: tabState,
          },
          {
            label: "Location",
            route: getLandholdingLocationRoute(landholdingUuid || "", currentUserType),
            state: tabState,
          },
          {
            label: "Inventory",
            route: getLandholdingInventoryRoute(landholdingUuid || "", currentUserType),
            state: tabState,
          },
          {
            label: "Documents",
            route: getLandholdingDocumentsRoute(landholdingUuid || "", currentUserType),
            state: tabState,
          },
          {
            label: "Tasks",
            route: getLandholdingTasksRoute(landholdingUuid || "", currentUserType),
            state: tabState,
          },
        ]
      : [
          {
            label: "Details",
            route: getLandholdingDashboardRoute(landholdingUuid || "", currentUserType),
            state: tabState,
          },
          {
            label: "Inventory",
            route: getLandholdingInventoryRoute(landholdingUuid || "", currentUserType),
            state: tabState,
          },
          {
            label: "Documents",
            route: getLandholdingDocumentsRoute(landholdingUuid || "", currentUserType),
            state: tabState,
          },
        ];
  }, [landholdingUuid, currentUserType, stateOrigin]);

  return {
    landholdingUuid,
    landholdingDetails,
    publicLandholdingDetails,
    handleBackClick,
    tabsList,
    backButtonText,
    projectsChartData,
    areaChartData,
    projectsChartColours,
    areaChartColours,
    isInventoryChartLoading,
    inventoryChartData,
    selectedUnitMeasurementData,
    updateInventoryChartData,
    unitMeasurementDropDownData,
    currentUserType,
    quickActionsMenuOptionsList,
    showQuickActionsMenu,
    setShowQuickActionsMenu,
    publicProjectsDevelopers,
    readGroupProjectsPermission,
  };
};
